import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError, firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthApiService } from '../services/auth-api.service';

let refreshInFlight: Promise<boolean> | null = null;

function tryRefresh(authService: AuthService, authApi: AuthApiService): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  // getRefreshToken() tries the in-memory signal first, then falls back to
  // document.cookie (readable in dev because the proxy strips HttpOnly; in every
  // other environment the cookie stays HttpOnly and this is always null). Call
  // the endpoint regardless — the httpOnly auth_refresh_token cookie rides along
  // via withCredentials and is what the backend actually validates.
  const token = authService.getRefreshToken();
  refreshInFlight = firstValueFrom(
    authApi.refresh({ refreshToken: token ?? '' })
  )
    .then((res) => {
      if (res?.success && res?.data) {
        authService.setSession(res);
        return true;
      }
      authService.clearSession();
      return false;
    })
    .catch(() => {
      // Any refresh failure (rejected token, network error, 5xx) ends the
      // session — the caller redirects to login right after this resolves.
      authService.clearSession();
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const authApi = inject(AuthApiService);
  const router = inject(Router);

  const isRefreshRequest = req.url.includes('/Auth/refresh');
  // Capture authenticated state before the request so we can decide whether to
  // redirect after a failed refresh attempt.
  const wasAuthenticated = !!authService.currentUser();

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Never try to refresh in response to the refresh request itself — would
      // cause an infinite loop and a spurious clearSession().
      if (err.status !== 401 || isRefreshRequest) {
        return throwError(() => err);
      }
      return from(tryRefresh(authService, authApi)).pipe(
        switchMap((success) => {
          if (success) {
            // Retry the original request — browser now has fresh cookies.
            return next(req);
          }
          // tryRefresh() always clears the session on failure — redirect
          // whenever the user was previously authenticated.
          if (wasAuthenticated) {
            void router.navigate(['/']);
          }
          return throwError(() => err);
        })
      );
    })
  );
};
